import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import api from "../services/api";
import { getToken } from "../services/auth";
import { jwtDecode } from "jwt-decode";
import { getMyPurchases } from "../services/purchase";
import Sidebar from "../components/Sidebar";
import CourseCard from "../components/CourseCard";
import { motion } from "framer-motion";
import { Sparkles, BookOpen, Search, Filter } from "lucide-react";
import toast, { Toaster } from 'react-hot-toast';

export default function Home() {
  const [user, setUser] = useState(null);
  const [allCourses, setAllCourses] = useState([]);
  const [purchasedCourses, setPurchasedCourses] = useState([]);
  const [loading, setLoading] = useState(true);

  const navigate = useNavigate();

  const loadData = useCallback(async () => {
    try {
      const [coursesRes, purchaseRes] = await Promise.all([
        api.get("/courses"),
        getMyPurchases()
      ]);
      setAllCourses(coursesRes.data);
      setPurchasedCourses(purchaseRes.data);
    } catch (err) {
      toast.error("Error loading courses");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    const token = getToken();
    if (!token) {
      navigate("/login");
      return;
    }
    const decoded = jwtDecode(token);
    if (decoded.role === "admin") {
      navigate("/admin");
      return;
    }
    setUser(decoded);
    loadData();
  }, [loadData, navigate]);

  const handleAddToCart = (course) => {
    const cart = JSON.parse(localStorage.getItem("cart") || "[]");
    if (cart.some(item => item.id === course.id)) {
      toast.error("Already in cart!");
      return;
    }
    cart.push({ id: course.id, title: course.title, price: course.price });
    localStorage.setItem("cart", JSON.stringify(cart));
    window.dispatchEvent(new Event("cartUpdated"));
    toast.success("Added to Cart! 🛒");
  };

  const Skeleton = () => (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
      {[1, 2, 3, 4, 5, 6].map(i => (
        <div key={i} className="card p-4 space-y-4 animate-pulse">
          <div className="aspect-video bg-slate-200 dark:bg-slate-800 rounded-xl" />
          <div className="h-4 w-3/4 bg-slate-200 dark:bg-slate-800 rounded" />
          <div className="h-3 w-full bg-slate-200 dark:bg-slate-800 rounded" />
          <div className="h-10 w-full bg-slate-200 dark:bg-slate-800 rounded-xl" />
        </div>
      ))}
    </div>
  );

  if (!user) return null;

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-950 flex">
      <Toaster position="top-center" />
      <Sidebar role={user.role} />

      <main className="flex-1 lg:ml-64 p-6 lg:p-10 space-y-12">
        {/* HERO SECTION */}
        <header className="flex flex-col md:flex-row md:items-center justify-between gap-8">
          <motion.div initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }}>
            <h1 className="text-3xl lg:text-4xl font-black text-slate-900 dark:text-white flex items-center gap-3">
              Welcome Back, {user.email.split('@')[0]} <Sparkles className="text-amber-400" />
            </h1>
            <p className="text-slate-500 dark:text-slate-400 mt-2 text-lg">Pick up where you left off or explore new skills.</p>
          </motion.div>

          <div className="flex gap-3">
            <div className="relative group">
              <Search size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400 group-focus-within:text-blue-500 transition-colors" />
              <input 
                type="text" 
                placeholder="Search courses..." 
                className="pl-11 pr-6 py-3 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-2xl outline-none focus:ring-4 focus:ring-blue-500/10 transition-all w-full md:w-64"
              />
            </div>
            <button className="p-3 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-2xl text-slate-500 hover:text-blue-600 transition-colors">
              <Filter size={20} />
            </button>
          </div>
        </header>

        {loading ? (
          <Skeleton />
        ) : (
          <div className="space-y-16">
            {/* CONTINUE LEARNING */}
            {purchasedCourses.length > 0 && (
              <section className="space-y-6">
                <div className="flex items-center justify-between">
                  <h2 className="text-xl font-extrabold text-slate-900 dark:text-white flex items-center gap-2">
                    <BookOpen className="text-blue-500" size={24} /> Continue Learning
                  </h2>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                  {purchasedCourses.map(c => (
                    <CourseCard 
                      key={c.id} 
                      course={c} 
                      isPurchased={true} 
                      onAction={() => navigate(`/course/${c.id}`)}
                    />
                  ))}
                </div>
              </section>
            )}

            {/* EXPLORE NEW COURSES */}
            <section className="space-y-6">
              <h2 className="text-xl font-extrabold text-slate-900 dark:text-white">Explore Courses</h2>
              {allCourses.length === 0 ? (
                <div className="card p-20 text-center space-y-4">
                  <div className="w-20 h-20 bg-slate-100 dark:bg-slate-800 rounded-full flex items-center justify-center mx-auto text-slate-400">
                    <Search size={40} />
                  </div>
                  <div className="space-y-1">
                    <h3 className="text-lg font-bold dark:text-white">No courses found</h3>
                    <p className="text-slate-500 dark:text-slate-400">We couldn't find any courses matching your criteria.</p>
                  </div>
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                  {allCourses.filter(c => !purchasedCourses.some(p => p.id === c.id)).map(c => (
                    <CourseCard 
                      key={c.id} 
                      course={c} 
                      isPurchased={false} 
                      onAction={() => navigate(`/course-detail/${c.id}`)}
                      onAddToCart={() => handleAddToCart(c)}
                    />
                  ))}
                </div>
              )}
            </section>
          </div>
        )}
      </main>
    </div>
  );
}
